import { toast } from "sonner";
import type { TFunction } from "i18next";
import { ApiError, getErrorMessage, ERROR_CODES } from "@tabipla/shared";

type ErrorToastOptions = {
  /** Callback for retry action */
  onRetry?: () => void;
  /** Override the default error message */
  message?: string;
  /** Duration in milliseconds (default: 5000) */
  duration?: number;
};

/**
 * Show an error toast with a translated message and optional actions.
 * Uses the structured error codes from ApiError to provide meaningful messages.
 *
 * @param error - The error object (ApiError, Error, or unknown)
 * @param t - The translation function from react-i18next
 * @param options - Additional options for the toast
 *
 * @example
 * ```tsx
 * try {
 *   await createTrip.mutateAsync(data);
 * } catch (error) {
 *   showErrorToast(error, t, { onRetry: () => handleSubmit() });
 * }
 * ```
 */
export function showErrorToast(
  error: unknown,
  t: TFunction,
  options: ErrorToastOptions = {}
): void {
  const { onRetry, message, duration = 5000 } = options;

  // Convert unknown error to Error
  const errorObj = error instanceof Error ? error : new Error(String(error));

  // Get translated message
  const translatedMessage = message || getErrorMessage(errorObj, t);

  // Determine error code for action buttons
  const errorCode = error instanceof ApiError ? error.code : undefined;

  // Build toast options
  const toastOptions: Parameters<typeof toast.error>[1] = {
    duration,
    description: getErrorDescription(errorCode, t),
  };

  // Add action button based on error type
  if (errorCode === ERROR_CODES.UNAUTHORIZED || errorCode === ERROR_CODES.SESSION_EXPIRED) {
    toastOptions.action = {
      label: t("error.actions.login", "ログイン"),
      onClick: () => {
        window.location.href = "/login";
      },
    };
  } else if (onRetry) {
    toastOptions.action = {
      label: t("error.actions.retry", "再試行"),
      onClick: onRetry,
    };
  }

  toast.error(translatedMessage, toastOptions);
}

/**
 * Get additional description for specific error codes.
 */
function getErrorDescription(errorCode: string | undefined, t: TFunction): string | undefined {
  if (!errorCode) return undefined;

  switch (errorCode) {
    case ERROR_CODES.NETWORK_ERROR:
      return t("error.descriptions.networkError", "インターネット接続を確認してください");
    case ERROR_CODES.UNAUTHORIZED:
    case ERROR_CODES.SESSION_EXPIRED:
      return t("error.descriptions.sessionExpired", "セッションが切れました");
    case ERROR_CODES.FORBIDDEN:
      return t("error.descriptions.forbidden", "この操作を行う権限がありません");
    case ERROR_CODES.NOT_FOUND:
      return t("error.descriptions.notFound", "データが見つかりませんでした");
    case ERROR_CODES.RATE_LIMIT_EXCEEDED:
      return t("error.descriptions.rateLimit", "しばらく待ってから再試行してください");
    default:
      return undefined;
  }
}

/**
 * Show a network error toast with retry action.
 */
export function showNetworkErrorToast(t: TFunction, onRetry?: () => void): void {
  toast.error(t("error.networkError", "ネットワークエラー"), {
    description: t("error.descriptions.networkError", "インターネット接続を確認してください"),
    action: onRetry
      ? {
          label: t("error.actions.retry", "再試行"),
          onClick: onRetry,
        }
      : undefined,
  });
}

/**
 * Show a validation error toast.
 */
export function showValidationErrorToast(t: TFunction, field?: string): void {
  const message = field
    ? t("error.validationErrorWithField", {
        field,
        defaultValue: `${field}の入力が正しくありません`,
      })
    : t("error.validationError", "入力内容を確認してください");

  toast.error(message);
}
